import { FC, Suspense } from "react"
import { useTranslation } from "react-i18next"
import { SpinnerCircular } from "spinners-react"
import { parseAxiosError } from "@/helpers/parseAxiosError"
import { safeLazy } from "@/helpers/safeLazy"
import { buildStatAmountRows } from "@/helpers/statFormat"
import { useCatalogs } from "@/hooks/useCatalogs"
import { useDevDebugVisible } from "@/hooks/useDevDebugVisible"
import { HeroWalletCounters } from "@/modals/constructor/hero/HeroModal/ui/HeroWalletCounters"
import { HeroVitalBars } from "@/providers/HeroVitals"
import { useGetHeroMeQuery } from "@/store/apis/heroAPI"
import { useGetMeQuery } from "@/store/apis/playerAPI"
import { Modal } from "@/ui/Modal"
import { StatBlock } from "@/ui/StatBlock"
import styles from "./HeroModal.module.scss"
import { levelLabel } from "./helpers"
import { ActiveEffectsSection } from "./ui/ActiveEffectsSection"
import { AttributeList } from "./ui/AttributeList"
import { SkillList } from "./ui/SkillList"
import { XpBar } from "./ui/XpBar"

const DebugPanel = safeLazy(() =>
  import("@/modals/constructor/hero/HeroModal/ui/DebugPanel").then((m) => ({
    default: m.DebugPanel
  }))
)

const modalType = "hero"

const DEBUG_REQUESTED = new URLSearchParams(window.location.search).has("debug")

export const HeroModal: FC = () => {
  const { t } = useTranslation("hero")
  const { data, error, isError, isFetching, refetch } = useGetHeroMeQuery()
  const { stats, skills: skillCatalog } = useCatalogs()

  const { data: me } = useGetMeQuery()
  const debugEnabled = useDevDebugVisible(DEBUG_REQUESTED)

  return (
    <Modal.Fullscreen type={modalType}>
      <div className={styles.HeroModal}>
        <h2 className={styles.HeroModal_title}>{t("title")}</h2>
        {isFetching && !data && (
          <div className={styles.HeroModal_center}>
            <SpinnerCircular
              size={48}
              thickness={120}
              speed={120}
              color="#f0f0f0"
              secondaryColor="#3a3a3a"
            />
          </div>
        )}
        {isError && !data && (
          <div className={styles.HeroModal_center}>
            <span className={styles.HeroModal_error}>
              {parseAxiosError(error).message || t("error.generic")}
            </span>
            <button
              type="button"
              className={styles.HeroModal_retry}
              onClick={() => void refetch()}
            >
              {t("error.retry")}
            </button>
          </div>
        )}
        {data && (
          <>
            <HeroVitalBars />
            <HeroWalletCounters />
            <div className={styles.HeroModal_level}>
              <span className={styles.HeroModal_levelValue}>
                {t("level.label")} {levelLabel(data.level)}
              </span>
              <XpBar level={data.level} />
            </div>
            <AttributeList
              attributes={data.attributes}
              bank={data.banks.attributePoints}
            />
            <SkillList
              skills={data.skills}
              catalog={skillCatalog}
              stats={stats}
              bank={data.banks.skillPoints}
            />
            <StatBlock
              title={t("modifiers.title")}
              rows={buildStatAmountRows(
                data.modifiers?.map(({ code, effective }) => ({
                  code,
                  value: effective
                })),
                stats
              )}
            />
            <StatBlock
              title={t("info.title")}
              rows={buildStatAmountRows(
                data.info?.map(({ code, effective }) => ({
                  code,
                  value: effective
                })),
                stats
              )}
            />
            <ActiveEffectsSection />
            {debugEnabled && me?.id && (
              <Suspense fallback={null}>
                <DebugPanel playerId={me.id} />
              </Suspense>
            )}
          </>
        )}
      </div>
    </Modal.Fullscreen>
  )
}
